import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { syncWallet } from '../../../redux/slices/walletSlice';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CopyIcon, RefreshCw } from 'lucide-react';
import { formatPrice, formatChange, getChangeColorClass, getChangePrefix } from '../utils/priceUtils';
import { truncateAddress } from '../../../utils/truncate';
import TransactionStatusLoader from '../../../components/TransactionStatusLoader';


const CoinHeader = ({ coin, mode, onRefresh, token }) => {
  const dispatch = useDispatch();
  const { syncStatus, lastSyncAt } = useSelector(state => state.wallet);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!coin) return null;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCopy = () => {

    navigator.clipboard.writeText(mode === 'private' ? coin?.walletAddress : coin?.address || coin?.walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };


  return (
    <div className="d-flex justify-content-between align-items-center mb-4">
      <div className="d-flex gap-3 align-items-center">
        <img
          src={coin.image}
          className="rounded-circle border"
          width={40}
          alt={coin.name}
        />
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h3 className="mb-0 font-bold text-xl">{coin.name}</h3>
            {coin.transactionStatus && (
              <TransactionStatusLoader transactionStatus={coin.transactionStatus} />
            )}
          </div>

          <div className='flex items-center gap-2'>
            <small>
              {coin.symbol} • {mode === 'private' ? truncateAddress(coin?.walletAddress) : truncateAddress(coin?.address)}
            </small>
            {((mode === 'private' && coin?.walletAddress?.length > 0) || (mode === 'public' && coin?.address?.length > 0)) && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="copy-tooltip">{copied ? "Copied!" : "Copy address"}</Tooltip>}
              >
                <button
                  className="copy-button"
                  style={{ minHeight: 'unset' }}
                  onClick={handleCopy}
                >
                  <CopyIcon size={18} />
                </button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      <div className="text-end d-flex gap-3 align-items-center">
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="refresh-tooltip">
              {mode === 'private' ? `Sync Wallet ${lastSyncAt ? `(Last: ${moment(lastSyncAt).format('HH:mm:ss')})` : ''}` : 'Refresh Coin'}
            </Tooltip>
          }
        >
          <button
            className="btn-icon"
            onClick={async () => {
              if (mode === 'private') {
                dispatch(syncWallet(mode));
              }
              await handleRefresh();
            }}
            disabled={isRefreshing || (mode === 'private' && syncStatus === 'syncing')}
            style={{
              backgroundColor: (isRefreshing || (mode === 'private' && syncStatus === 'syncing')) ? '#f0f0f0' : 'transparent',
              opacity: (isRefreshing || (mode === 'private' && syncStatus === 'syncing')) ? 0.7 : 1,
              cursor: (isRefreshing || (mode === 'private' && syncStatus === 'syncing')) ? 'not-allowed' : 'pointer'
            }}
          >
            <RefreshCw
              className={`w-6 h-6 ${(isRefreshing || (mode === 'private' && syncStatus === 'syncing')) ? 'refresh-rotating' : ''}`}
            />
          </button>
        </OverlayTrigger>
        <div>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="coin-price-tooltip">
                Current Price: ${formatPrice(coin.price?.price)}
              </Tooltip>
            }
          >
            <div className="text-xl mb-0 fw-bold" style={{ color: 'var(--text-primary)' }}>
              ${formatPrice(coin.price?.price)}
            </div>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="coin-change-tooltip">
                24h Change: {getChangePrefix(coin.price?.change)}
                {formatChange(coin.price?.change)}%
              </Tooltip>
            }
          >
            <div className={`small fw-semibold ${getChangeColorClass(coin.price?.change)}`}>
              {getChangePrefix(coin.price?.change)}
              {formatChange(coin.price?.change)}%
            </div>
          </OverlayTrigger>
        </div>
      </div>
    </div>
  );
};

export default CoinHeader;
